import { useState, useEffect, useRef } from 'react'
import { Box, DialogContent, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Dialog from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import SharedButton from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import PlusIcon from '@hapana/shared/web/styles/assets/svgs/plus-small.svg'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import { format } from 'date-fns'
import { localTimeFormat } from '@hapana/shared/utils/date.util'
import { getCurrencySymbol } from '@hapana/shared/utils/string.util'
import { useQuery, resolved } from '@hapana/shared/web/services/gqty'

import {
  AGREEMENT_TEMPLATE_TAGS_REGEX,
  AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX,
} from '@hapana/shared/schemas/users/client-agreements-schemas'

import useSignatureCaptureStore from '../../../../../stores/signature-capture.store'
import AddSignatureDialog from '../../../../retail-settings/checkout/components/add-signature-dialog/add-signature-dialog.component'
import { TRANSLATION_KEYS } from '../documents.constants'
import type { SignAgreementDialogProps, SignatureSlot } from '../documents.types'
import { styles } from './sign-agreement-dialog.styles'

type ContentSegment = { type: 'html'; html: string } | { type: 'slot'; slot: SignatureSlot }

// Matches numbered multi-sig shortcodes: <SIGNATURES1>, <INITIAL1>, etc.
const NUMBERED_SHORTCODE_REGEX = /<(SIGNATURES|INITIAL)(\d+)>/gi
// Matches HTML-encoded numbered shortcodes: &lt;SIGNATURES1&gt;, &lt;INITIAL1&gt;
const NUMBERED_SHORTCODE_ENCODED_REGEX =
  /(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(SIGNATURES|INITIAL)(\d+)&gt;(?:<\/span>)?/gi

// Parse content into alternating HTML segments and signature/initial slots
const parseContentSegments = (
  content: string
): { segments: ContentSegment[]; slots: SignatureSlot[] } => {
  const slots: SignatureSlot[] = []
  const segments: ContentSegment[] = []
  let lastIndex = 0
  let match

  // Check for numbered multi-sig shortcodes first (<SIGNATURES1>, <INITIAL1>)
  const numberedRaw = new RegExp(NUMBERED_SHORTCODE_REGEX.source, NUMBERED_SHORTCODE_REGEX.flags)
  const numberedEncoded = new RegExp(
    NUMBERED_SHORTCODE_ENCODED_REGEX.source,
    NUMBERED_SHORTCODE_ENCODED_REGEX.flags
  )
  const hasNumberedRaw = numberedRaw.test(content)
  numberedRaw.lastIndex = 0

  if (hasNumberedRaw || numberedEncoded.test(content)) {
    numberedEncoded.lastIndex = 0
    const numberedRegex = hasNumberedRaw ? numberedRaw : numberedEncoded

    while ((match = numberedRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'html', html: content.slice(lastIndex, match.index) })
      }
      const tagName = match[1]!.toUpperCase() // 'SIGNATURES' or 'INITIAL'
      const num = match[2]! // '1', '2', etc.
      const slotType: 'SIGNATURE' | 'INITIAL' = tagName === 'SIGNATURES' ? 'SIGNATURE' : 'INITIAL'
      const slotKey = `${tagName.toLowerCase()}${num}` // e.g. 'signatures1', 'initial1'

      const slot: SignatureSlot = {
        index: slots.length,
        type: slotType,
        dataUrl: null,
        slotKey,
      }
      slots.push(slot)
      segments.push({ type: 'slot', slot })
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < content.length) {
      segments.push({ type: 'html', html: content.slice(lastIndex) })
    }

    if (segments.length > 0) return { segments, slots }
  }

  // Fall back to unnumbered tags: <SIGNATURE>, <INITIAL>
  const rawRegex = new RegExp(
    AGREEMENT_TEMPLATE_TAGS_REGEX.source,
    AGREEMENT_TEMPLATE_TAGS_REGEX.flags
  )
  const encodedRegex = new RegExp(
    AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX.source,
    AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX.flags
  )

  const hasRaw = rawRegex.test(content)
  rawRegex.lastIndex = 0
  const regex = hasRaw ? rawRegex : encodedRegex

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'html', html: content.slice(lastIndex, match.index) })
    }

    const slot: SignatureSlot = {
      index: slots.length,
      type: match[1]!.toUpperCase() as 'SIGNATURE' | 'INITIAL',
      dataUrl: null,
    }
    slots.push(slot)
    segments.push({ type: 'slot', slot })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'html', html: content.slice(lastIndex) })
  }

  if (segments.length === 0) {
    segments.push({ type: 'html', html: content })
  }

  return { segments, slots }
}

const SignAgreementDialog: React.FC<SignAgreementDialogProps> = ({
  agreementId,
  agreementName,
  clientId,
  siteId,
  onSign,
  onCreateAndSign,
  onSuccess,
  agreementContent: initialContent,
  onSignaturesCollected,
  readOnly = false,
  savedSignatures,
  additionalShortcodes,
}) => {
  const { t } = useTranslation('core')
  const { popDialog, pushDialog } = useDialog()
  const { openToast } = useToast()
  const query = useQuery({ suspense: false })
  const signatureImages = useSignatureCaptureStore((state) => state.signatureImages)
  const setActiveSlotIndex = useSignatureCaptureStore((state) => state.setActiveSlotIndex)

  const [agreementContent, setAgreementContent] = useState<string | null>(initialContent ?? null)
  const [isLoadingContent, setIsLoadingContent] = useState(!initialContent)
  const [loadError, setLoadError] = useState(false)
  const [segments, setSegments] = useState<ContentSegment[]>([])
  const [slots, setSlots] = useState<SignatureSlot[]>([])
  const [isSigning, setIsSigning] = useState(false)
  const [fetchedSignatureUrls, setFetchedSignatureUrls] = useState<string[] | null>(null)

  // Track the last content we processed for shortcodes to avoid re-processing resolved content
  const processedContentRef = useRef<string | null>(null)

  // Resolve agreement shortcodes (CLIENT_*, BUSINESS_*, SITE_*, etc.) when content is available.
  // Mirrors the shortcode resolution logic in checkout.component.jsx but fetches client/site data
  // internally using the available clientId and siteId props.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!agreementContent) return
    if (processedContentRef.current === agreementContent) return

    // Only proceed if there are resolvable shortcodes (ignore signing tags and AGREEMENT_ID)
    const hasResolvableShortcodes =
      /<(?!AGREEMENT_ID>|SIGNATURES?\d*>|INITIAL\d*>)[A-Z][A-Z0-9_]*>/.test(agreementContent) ||
      /&lt;(?!AGREEMENT_ID&gt;|SIGNATURES?\d*&gt;|INITIAL\d*&gt;)[A-Z][A-Z0-9_]*&gt;/.test(
        agreementContent
      )
    if (!hasResolvableShortcodes) {
      processedContentRef.current = agreementContent
      return
    }

    const resolveShortcodes = async () => {
      try {
        const data = await resolved(
          () => {
            const clientResult = query.client({ id: clientId, siteId })
            const client = clientResult?.client
            const siteObj = query.site({ siteId })

            return {
              client: {
                id: client?.id,
                firstName: client?.firstName,
                lastName: client?.lastName,
                name: client?.name,
                email: client?.email,
                mobilePhoneNumber: client?.mobilePhoneNumber,
                gender: client?.gender,
                dateOfBirth: client?.dateOfBirth,
                address: client?.address
                  ? {
                      streetNumber: client.address.streetNumber,
                      streetAddress: client.address.streetAddress,
                      city: client.address.city,
                      state: client.address.state,
                      zipCode: client.address.zipCode,
                    }
                  : null,
              },
              site: {
                name: siteObj?.name,
                phone: siteObj?.phone,
                businessEmail: siteObj?.businessEmail,
                website: siteObj?.website,
                addressStreet: siteObj?.addressStreet,
                addressStreet2: siteObj?.addressStreet2,
                addressCity: siteObj?.addressCity,
                addressState: siteObj?.addressState,
                addressPostalCode: siteObj?.addressPostalCode,
                timezone: siteObj?.timezone,
                currency: siteObj?.settings?.location?.configurations?.currency,
                timeFormat: siteObj?.settings?.location?.configurations?.timeFormat,
                logoImageUrl:
                  siteObj
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ?.logoImage({ input: { format: 'png' as any, type: 'original' as any } })
                    ?.url ?? null,
              },
            }
          },
          { refetch: true }
        )

        const escapeHtml = (value: string) =>
          value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')

        const clientAddress = data?.client?.address
          ? [
              [data.client.address.streetNumber, data.client.address.streetAddress]
                .filter(Boolean)
                .join(' '),
              data.client.address.city,
              data.client.address.state,
              data.client.address.zipCode,
            ]
              .filter(Boolean)
              .join(', ') || null
          : null

        const siteAddress = [
          data?.site?.addressStreet,
          data?.site?.addressStreet2,
          data?.site?.addressCity,
          data?.site?.addressState,
          data?.site?.addressPostalCode,
        ]
          .filter(Boolean)
          .join(', ')

        const siteTimezone = data?.site?.timezone

        const shortcodeValues: Record<string, string | null | undefined> = {
          TODAY_DATE: format(new Date(), 'd MMM yyyy'),
          AGREEMENT_ID: agreementId ?? null,
          CLIENT_ID: data?.client?.id,
          CLIENT_FIRST_NAME: data?.client?.firstName,
          CLIENT_LAST_NAME: data?.client?.lastName,
          CLIENT_NAME:
            data?.client?.name ||
            [data?.client?.firstName, data?.client?.lastName].filter(Boolean).join(' '),
          CLIENT_EMAIL: data?.client?.email,
          CLIENT_PHONE: data?.client?.mobilePhoneNumber,
          CLIENT_GENDER: data?.client?.gender,
          CLIENT_DOB: data?.client?.dateOfBirth,
          CLIENT_ADDRESS: clientAddress,
          BUSINESS_NAME: data?.site?.name,
          BUSINESS_PHONE: data?.site?.phone,
          BUSINESS_EMAIL: data?.site?.businessEmail,
          BUSINESS_WEBSITE: data?.site?.website,
          SITE_ADDRESS: siteAddress || null,
          CURRENCY_SYMBOL: data?.site?.currency ? getCurrencySymbol(data.site.currency) : null,
          LOCAL_TIME: siteTimezone
            ? localTimeFormat(new Date(), siteTimezone, data?.site?.timeFormat)
            : null,
        }

        let resolvedContent = agreementContent
        for (const [key, value] of Object.entries(shortcodeValues)) {
          if (value) {
            const escaped = escapeHtml(String(value))
            resolvedContent = resolvedContent
              .replaceAll(`&lt;${key}&gt;`, escaped)
              .replaceAll(`<${key}>`, escaped)
          }
        }

        // Apply additional shortcodes (e.g. package terms from the change-plan flow)
        if (additionalShortcodes) {
          for (const [key, value] of Object.entries(additionalShortcodes)) {
            if (value) {
              const escaped = escapeHtml(String(value))
              resolvedContent = resolvedContent
                .replaceAll(`&lt;${key}&gt;`, escaped)
                .replaceAll(`<${key}>`, escaped)
            }
          }
        }

        // BUSINESS_LOGO requires img tag injection (not plain text)
        if (data?.site?.logoImageUrl) {
          const logoImg = `<img src="${escapeHtml(data.site.logoImageUrl)}" alt="${escapeHtml(t('clients.documents.signAgreement.businessLogoAlt'))}" style="max-width:200px;" />`
          resolvedContent = resolvedContent
            .replaceAll('&lt;BUSINESS_LOGO&gt;', logoImg)
            .replaceAll('<BUSINESS_LOGO>', logoImg)
        }

        // Strip unresolved shortcodes, preserving signing tags and AGREEMENT_ID
        resolvedContent = resolvedContent
          .replaceAll(
            /&lt;(?!AGREEMENT_ID&gt;|SIGNATURES?\d*&gt;|INITIAL\d*&gt;)[A-Z][A-Z0-9_]*&gt;/g,
            ''
          )
          .replaceAll(/<(?!AGREEMENT_ID>|SIGNATURES?\d*>|INITIAL\d*>)[A-Z][A-Z0-9_]*>/g, '')

        processedContentRef.current = resolvedContent
        setAgreementContent(resolvedContent)
      } catch {
        // Mark as processed to prevent infinite retries on error
        processedContentRef.current = agreementContent
      }
    }

    resolveShortcodes()
  }, [agreementContent, clientId, siteId])

  // Parse content into segments when available
  useEffect(() => {
    if (!agreementContent) return
    const parsed = parseContentSegments(agreementContent)

    if (parsed.slots.length === 0) {
      // No <SIGNATURE>/<INITIAL> tags found — inject an implicit signature slot at the end
      // so tagless agreements can still be signed during checkout
      const implicitSlot: SignatureSlot = {
        index: 0,
        type: 'SIGNATURE',
        dataUrl: null,
      }
      setSegments([...parsed.segments, { type: 'slot', slot: implicitSlot }])
      setSlots([implicitSlot])
    } else {
      setSegments(parsed.segments)
      setSlots(parsed.slots)
    }
  }, [agreementContent])

  // Fetch content (and signature URLs in read-only mode) from backend if not provided directly
  useEffect(() => {
    if (initialContent) return

    if (!agreementId) {
      setLoadError(true)
      setIsLoadingContent(false)
      return
    }

    resolved(() => {
      const agreement = query.clientAgreement({
        agreementId: agreementId!,
        clientId,
        siteId,
      })
      return {
        // When additionalShortcodes are provided the caller needs to replace package-level
        // shortcodes (AMOUNT, BILLING_CYCLE, etc.) client-side. renderedContent has those
        // stripped by the server, so use the raw agreementContent instead so every placeholder
        // is still present for client-side resolution.
        content: additionalShortcodes
          ? agreement?.agreementContent
          : (agreement?.renderedContent ?? agreement?.agreementContent),
        signatureUrls: readOnly ? agreement?.signatureImageUrls : null,
      }
    })
      .then((result) => {
        if (result?.content) {
          setAgreementContent(result.content)
          if (result.signatureUrls?.length) {
            setFetchedSignatureUrls([...result.signatureUrls])
          }
        } else {
          setLoadError(true)
        }
      })
      .catch(() => {
        setLoadError(true)
      })
      .finally(() => {
        setIsLoadingContent(false)
      })
  }, [agreementId, clientId, siteId, initialContent, readOnly])

  const handleSlotClick = (slotIndex: number) => {
    setActiveSlotIndex(slotIndex)
    pushDialog(AddSignatureDialog, {})
  }

  const areAllSlotsSigned = () => {
    if (slots.length === 0) return false
    return slots.every((slot) => signatureImages[slot.index])
  }

  const handleSign = async () => {
    if (!areAllSlotsSigned()) return

    setIsSigning(true)
    try {
      const signatures = slots.map((slot) => {
        const dataUrl = signatureImages[slot.index]
        if (!dataUrl) throw new Error('Missing signature')
        return dataUrl.replace(/^data:image\/png;base64,/, '')
      })

      let resultAgreementId: string | undefined

      if (agreementId && onSign) {
        // Path B: Agreement already exists (from siteAgreementTemplate) — sign it
        await onSign({
          args: {
            agreementId,
            clientId,
            siteId,
            signatures,
            agreementMethod: 'SIGNATURE',
            userAgent: navigator.userAgent,
          },
        })
        resultAgreementId = agreementId
      } else if (onCreateAndSign && agreementContent) {
        // Path A: No agreement exists yet (inline agreementText) — create and sign in one step
        const result = (await onCreateAndSign({
          args: {
            clientId,
            siteId,
            agreementName,
            agreementContent,
            signatures,
            agreementMethod: 'SIGNATURE',
            userAgent: navigator.userAgent,
          },
        })) as { agreementId?: string } | undefined
        resultAgreementId = result?.agreementId
      }

      onSignaturesCollected?.(signatures, resultAgreementId)

      openToast({
        title: t(TRANSLATION_KEYS.SIGN_AGREEMENT.SUCCESS),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      })

      onSuccess?.()
      popDialog()
    } catch {
      openToast({
        title: t(TRANSLATION_KEYS.SIGN_AGREEMENT.ERROR),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
      setIsSigning(false)
    }
  }

  if (isLoadingContent) {
    return (
      <Dialog
        title={agreementName}
        closeType={CLOSE_TYPES.ICON}
        closeAction={popDialog}
        size={SIZES.MD}
        hasMobileBottomSheet
      >
        <DialogContent sx={styles.content}>
          <Loading label={t(TRANSLATION_KEYS.SIGN_AGREEMENT.LOADING)} />
        </DialogContent>
      </Dialog>
    )
  }

  if (loadError || !agreementContent) {
    return (
      <Dialog
        title={agreementName}
        closeType={CLOSE_TYPES.ICON}
        closeAction={popDialog}
        size={SIZES.MD}
        hasMobileBottomSheet
      >
        <DialogContent sx={styles.content}>
          <Typography color="error">{t(TRANSLATION_KEYS.SIGN_AGREEMENT.LOAD_ERROR)}</Typography>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      title={agreementName}
      closeType={CLOSE_TYPES.ICON}
      closeAction={popDialog}
      size={SIZES.MD}
      hasMobileBottomSheet
      {...(!readOnly && {
        primaryAction: {
          label: t(TRANSLATION_KEYS.SIGN_AGREEMENT.SIGN),
          action: handleSign,
          theme: BUTTON_THEME.PRIMARY,
          props: { disabled: !areAllSlotsSigned() },
          isLoading: isSigning,
        },
      })}
    >
      <DialogContent sx={styles.content}>
        <Box sx={styles.agreementContent}>
          {segments.map((segment, i) => {
            if (segment.type === 'html') {
              return (
                <Box
                  key={`html-${i}`}
                  dangerouslySetInnerHTML={{ __html: segment.html }}
                  sx={styles.htmlSegment}
                />
              )
            }

            const { slot } = segment
            const isSignature = slot.type === 'SIGNATURE'
            const capturedImage = readOnly
              ? slot.slotKey
                ? // Numbered multi-sig: find URL by slot key in GCS filename.
                  // Fall back to sequential index for agreements signed via signClientAgreement
                  // or createAndSignAgreement which store files as 'agreementId-signature-0.png'
                  // (no slot key) rather than 'agreementId-slot-signatures1.png'.
                  (fetchedSignatureUrls?.find((url) => url.includes(`-slot-${slot.slotKey}.`)) ??
                  fetchedSignatureUrls?.[slot.index] ??
                  null)
                : // Single-sig: use sequential index
                  (fetchedSignatureUrls?.[slot.index] ??
                  (savedSignatures?.[slot.index]
                    ? `data:image/png;base64,${savedSignatures[slot.index]}`
                    : null))
              : signatureImages[slot.index]
            const label = isSignature
              ? t('checkout:membershipAgreementsStep.addSignature')
              : t('checkout:membershipAgreementsStep.addInitials')

            if (capturedImage) {
              if (readOnly) {
                return (
                  <Box key={`slot-${slot.index}`} sx={styles.slotInlineContainer}>
                    <Box
                      component="img"
                      src={capturedImage}
                      alt={label}
                      sx={isSignature ? styles.readOnlySignature : styles.readOnlyInitials}
                    />
                  </Box>
                )
              }

              return (
                <Box key={`slot-${slot.index}`} sx={styles.slotInlineContainer}>
                  <Box
                    onClick={() => handleSlotClick(slot.index)}
                    sx={[
                      styles.signedSlot,
                      isSignature ? styles.signedSignatureSlot : styles.signedInitialsSlot,
                    ]}
                  >
                    <Box component="img" src={capturedImage} alt={label} sx={styles.signedImage} />
                  </Box>
                </Box>
              )
            }

            return (
              <Box key={`slot-${slot.index}`} sx={styles.slotInlineContainer}>
                <SharedButton
                  label={label}
                  variant={BUTTON_VARIANTS.BOX}
                  sx={isSignature ? styles.signatureButton : styles.initialsButton}
                  onClick={() => handleSlotClick(slot.index)}
                  icon={PlusIcon}
                  disabled={readOnly}
                />
              </Box>
            )
          })}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default SignAgreementDialog
